"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ssoSessionLogin = exports.getLoginData = exports.AuthProvider = exports.useAuthContext = exports.AuthContext = void 0;
const styled_components_1 = require("styled-components");
const auth0_js_1 = require("auth0-js");
const auth0_lock_1 = __importDefault(require("auth0-lock"));
const react_1 = __importDefault(require("react"));
const getAuth0Options = (theme, audience, redirectFromLoginUrl, allowedConnections, defaultEmail) => ({
    allowedConnections,
    allowForgotPassword: true,
    allowSignUp: false,
    autoclose: true,
    autofocus: true,
    avatar: null,
    container: "AuthContainer",
    hashCleanup: false,
    rememberLastLogin: false,
    usernameStyle: "email",
    prefill: { email: defaultEmail },
    forgotPasswordLink: `${document.location.origin}/SendChangePassword`,
    auth: {
        audience: audience,
        autoParseHash: false,
        redirect: true,
        redirectUrl: redirectFromLoginUrl,
        responseType: "token id_token"
    },
    languageDictionary: {
        emailInputPlaceholder: "Email",
        passwordInputPlaceholder: "Password",
        title: theme["name"]
    },
    theme: {
        logo: theme["app-logo-color-url"],
        primaryColor: theme["app-link-color"]
    }
});
/**
 * Generates a psuedo random value from the specified number of unsigned integers
 * @param count number of unsigned integers to use
 */
function generateUint32Crypt(count) {
    const intCount = Math.round(count);
    if (count > 0) {
        return Array.prototype.map
            .call(window.crypto.getRandomValues(new Uint32Array(intCount)), x => x.toString().padStart(10, "0"))
            .join("");
    }
    else {
        return "";
    }
}
exports.AuthContext = react_1.default.createContext({});
exports.AuthContext.displayName = "AuthContext";
exports.useAuthContext = () => react_1.default.useContext(exports.AuthContext);
exports.AuthProvider = (props) => {
    const defaultTheme = react_1.default.useContext(styled_components_1.ThemeContext);
    const theme = props.theme === undefined ? defaultTheme : props.theme;
    const options = react_1.default.useMemo(() => getAuth0Options(theme, props.audience, props.redirectFromLoginUrl, props.allowedConnections, props.defaultEmail), [
        props.audience,
        props.redirectFromLoginUrl,
        theme,
        props.allowedConnections,
        props.defaultEmail
    ]);
    const lock = react_1.default.useMemo(() => new auth0_lock_1.default(props.clientId, props.domain, options), [options, props]);
    const getAuthResult = () => {
        const authResult = localStorage.getItem("authResult");
        if (authResult === null) {
            return null;
        }
        return JSON.parse(authResult);
    };
    const getIsAuthenticated = () => {
        const result = getAuthResult();
        const ns = "https://servicepro10.com";
        if (result === null ||
            !result.idTokenPayload ||
            result.idTokenPayload.iss !== `https://${props.domain}/` ||
            result.idTokenPayload.aud !== props.clientId ||
            result.accessTokenPayload.exp * 1000 <= Date.now() ||
            result.idTokenPayload.exp * 1000 <= Date.now() ||
            !result.accessTokenPayload ||
            !result.accessTokenPayload[`${ns}/app`] ||
            !result.accessTokenPayload[`${ns}/email`] ||
            !result.accessTokenPayload[`${ns}/brand`] ||
            result.accessTokenPayload.azp !== props.clientId) {
            return false;
        }
        const lsCompanyId = localStorage.getItem("Company-Id") || "-1";
        const companyId = parseInt(lsCompanyId);
        if (companyId === -1) {
            return false;
        }
        return true;
    };
    const [isAuthenticated, setIsAuthenticated] = react_1.default.useState(getIsAuthenticated());
    const authResult = getAuthResult();
    const defaultState = isAuthenticated ? authResult.state : "";
    const [state] = react_1.default.useState(defaultState);
    const defaultToken = isAuthenticated ? authResult.accessToken : "";
    const [token] = react_1.default.useState(defaultToken);
    const value = react_1.default.useMemo(() => {
        const logout = (redirectUrl) => {
            const url = redirectUrl === undefined
                ? `${document.location.origin}/Logout`
                : redirectUrl;
            localStorage.clear();
            lock.logout({
                returnTo: url
            });
            return react_1.default.createElement(react_1.default.Fragment, null);
        };
        return {
            lock,
            state,
            token,
            isAuthenticated,
            setIsAuthenticated,
            profile: authResult && authResult.idTokenPayload,
            logout
        };
    }, [authResult, isAuthenticated, lock, state, token]);
    return (react_1.default.createElement(exports.AuthContext.Provider, { value: value }, props.children));
};
exports.getLoginData = () => {
    const authResult = localStorage.getItem("authResult");
    if (authResult === null) {
        return null;
    }
    return JSON.parse(authResult);
};
/**
 * Authorizes the client using an existing session with an identity provider.
 * This enforces a standard browser flow instead of using identity provider
 * initiated requests.
 * @param domain Auth0 domain
 * @param clientId Target client ID
 * @param audience Target audience
 * @param redirectUri URI to redirect to after authorization
 * @param connection Specific connection to use when authorizing
 */
function ssoSessionLogin(domain, clientId, audience, redirectUri, connection) {
    const webAuth = new auth0_js_1.WebAuth({
        domain: domain,
        clientID: clientId
    });
    localStorage.clear();
    const state = generateUint32Crypt(15);
    const nonce = generateUint32Crypt(15);
    webAuth.authorize({
        state: state,
        nonce: nonce,
        audience: audience,
        clientID: clientId,
        redirectUri: redirectUri,
        connection: connection,
        responseType: "token id_token",
        scope: "profile email openid"
    });
}
exports.ssoSessionLogin = ssoSessionLogin;
//# sourceMappingURL=useAuth0.js.map